import type { PixelPoint, SheetScale } from '@otto/onscreen-engine';

// PDF.js scale=1 viewport is 72 DPI (1 PDF point = 1/72"). Click coordinates
// are normalized to this space so pixelsPerFoot stays valid when the user
// zooms or the container resizes.
export const PDF_NATIVE_DPI = 72;

export type Tool = 'pointer' | 'calibrate' | 'linear' | 'polygon' | 'count';

export type CalibStatus =
  | 'idle'
  | 'awaiting_first'
  | 'awaiting_second'
  | 'awaiting_distance';

export type ItemKind = 'linear' | 'polygon' | 'count';

export interface TakeoffItem {
  id: string;
  kind: ItemKind;
  // PDF native coords (scale=1 / 72 DPI). Multiply by renderScale at draw time.
  points: PixelPoint[];
  label?: string;
}

export interface CivilPageData {
  scale: SheetScale | null;
  items: TakeoffItem[];
}

export interface CivilJobData {
  pageIndex: number;
  pages: Record<number, CivilPageData>;
}

export const EMPTY_PAGE: CivilPageData = { scale: null, items: [] };

export const EMPTY_JOB: CivilJobData = { pageIndex: 0, pages: {} };

export function getPage(d: CivilJobData, pageIndex: number): CivilPageData {
  return d.pages[pageIndex] ?? EMPTY_PAGE;
}

export function setPage(
  d: CivilJobData,
  pageIndex: number,
  patch: Partial<CivilPageData>,
): CivilJobData {
  const current = getPage(d, pageIndex);
  return {
    ...d,
    pages: {
      ...d.pages,
      [pageIndex]: { ...current, ...patch },
    },
  };
}

export function hasAnyContent(d: CivilJobData): boolean {
  for (const p of Object.values(d.pages)) {
    if (p.scale) return true;
    if (p.items.length > 0) return true;
  }
  return false;
}

export function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `item_${Math.random().toString(36).slice(2, 10)}`;
}

export function parseFeetInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return n;
}

// ─── Persistence ────────────────────────────────────────────────────────────

function serializeScale(scale: SheetScale | null): unknown {
  if (!scale) return null;
  return {
    pixelsPerFoot: scale.pixelsPerFoot,
    dpi: scale.dpi,
    source: scale.source,
    scaleString: scale.scaleString ?? null,
  };
}

function serializeItem(it: TakeoffItem): Record<string, unknown> {
  return {
    id: it.id,
    kind: it.kind,
    points: it.points.map((p) => ({ x: p.x, y: p.y })),
    label: it.label ?? null,
  };
}

export function serializeJobData(d: CivilJobData): Record<string, unknown> {
  const pages: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(d.pages)) {
    pages[k] = {
      scale: serializeScale(v.scale),
      items: v.items.map(serializeItem),
    };
  }
  return {
    version: 2,
    pageIndex: d.pageIndex,
    pages,
  };
}

function restoreScale(raw: unknown): SheetScale | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (
    typeof s.pixelsPerFoot !== 'number' ||
    typeof s.dpi !== 'number' ||
    (s.source !== 'preset' && s.source !== 'calibrated' && s.source !== 'extracted')
  ) {
    return null;
  }
  return {
    pixelsPerFoot: s.pixelsPerFoot,
    dpi: s.dpi,
    source: s.source,
    scaleString: typeof s.scaleString === 'string' ? s.scaleString : undefined,
  };
}

function restoreItem(raw: unknown): TakeoffItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string') return null;
  if (r.kind !== 'linear' && r.kind !== 'polygon' && r.kind !== 'count') return null;
  if (!Array.isArray(r.points)) return null;
  const points: PixelPoint[] = [];
  for (const p of r.points) {
    if (p && typeof p === 'object' && typeof (p as { x: unknown }).x === 'number' && typeof (p as { y: unknown }).y === 'number') {
      const pp = p as { x: number; y: number };
      points.push({ x: pp.x, y: pp.y });
    }
  }
  return {
    id: r.id,
    kind: r.kind,
    points,
    label: typeof r.label === 'string' ? r.label : undefined,
  };
}

export function restoreJobData(raw: Record<string, unknown>): CivilJobData {
  const pageIndex = typeof raw.pageIndex === 'number' ? raw.pageIndex : 0;

  // v2 shape: { version: 2, pageIndex, pages: { [n]: { scale, items } } }
  if (raw.version === 2 && raw.pages && typeof raw.pages === 'object') {
    const pages: Record<number, CivilPageData> = {};
    for (const [k, v] of Object.entries(raw.pages as Record<string, unknown>)) {
      const idx = Number(k);
      if (!Number.isFinite(idx) || !v || typeof v !== 'object') continue;
      const pageRaw = v as Record<string, unknown>;
      const items: TakeoffItem[] = Array.isArray(pageRaw.items)
        ? pageRaw.items.map(restoreItem).filter((it): it is TakeoffItem => it !== null)
        : [];
      pages[idx] = { scale: restoreScale(pageRaw.scale), items };
    }
    return { pageIndex, pages };
  }

  // v1 migration: flat { pageIndex, scale } → put scale on that page
  const scale = restoreScale(raw.scale);
  if (scale) {
    return {
      pageIndex,
      pages: { [pageIndex]: { scale, items: [] } },
    };
  }

  return { pageIndex, pages: {} };
}
